import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Image as ImageIcon,
  Upload,
  X,
  Paperclip,
  FileText,
  Check,
  Folder,
  FolderPlus,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const MEDIA_IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

/** Baut Pfade wie „Bibliothek / Kunden / Logos“ aus der Ordnerliste. */
export function createMediaFolderPathResolver(folders = []) {
  const byId = new Map();
  folders.forEach((f) => {
    if (f?.id == null) return;
    byId.set(Number(f.id), f);
  });
  const cache = new Map();

  return (folderId) => {
    if (folderId == null || folderId === '' || folderId === 'root') return 'Bibliothek';
    const startId = Number(folderId);
    if (!Number.isFinite(startId) || startId <= 0) return 'Bibliothek';
    if (cache.has(startId)) return cache.get(startId);

    const parts = [];
    let cur = byId.get(startId);
    const seen = new Set();
    while (cur && !seen.has(Number(cur.id))) {
      seen.add(Number(cur.id));
      parts.unshift(String(cur.name || 'Ordner'));
      cur = cur.parentId != null ? byId.get(Number(cur.parentId)) : null;
    }
    const path = parts.length ? `Bibliothek / ${parts.join(' / ')}` : 'Bibliothek';
    cache.set(startId, path);
    return path;
  };
}
export const MEDIA_FILE_ACCEPT = [
  MEDIA_IMAGE_ACCEPT,
  'application/pdf',
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.zip',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/rtf',
  'application/zip',
].join(',');

export const mediaSrc = (item) => {
  const path = item?.path;
  if (path && String(path).startsWith('/')) return path;
  if (item?.url && String(item.url).startsWith('/')) return item.url;
  // Absolute localhost-URLs nicht für <img> nutzen — relativer Pfad aus der Absolute ableiten
  if (item?.url && /^https?:\/\//i.test(item.url)) {
    try {
      const u = new URL(item.url);
      if (u.pathname.startsWith('/api/m/')) return `${u.pathname}${u.search || ''}`;
    } catch (_) {
      /* ignore */
    }
  }
  return item?.url || '';
};

function isLoopbackMediaUrl(url) {
  try {
    const host = new URL(url).hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
  } catch (_) {
    return false;
  }
}

/**
 * Absolute Medien-URL für E-Mails / Zwischenablage.
 * publicBaseUrl: optionale Server-URL (PUBLIC_BASE_URL), überschreibt localhost.
 */
export const mediaAbsoluteUrl = (item, publicBaseUrl = '') => {
  if (!item) return '';
  const relPath = mediaSrc(item);
  const configuredBase = String(publicBaseUrl || '')
    .trim()
    .replace(/\/+$/, '');

  if (configuredBase && relPath && relPath.startsWith('/')) {
    return `${configuredBase}${relPath}`;
  }

  const abs = item.url && /^https?:\/\//i.test(item.url) ? item.url : '';
  if (abs && !isLoopbackMediaUrl(abs)) return abs;

  if (relPath && relPath.startsWith('/') && typeof window !== 'undefined' && window.location?.origin) {
    // Browser auf echtem Host: nie localhost aus der API übernehmen
    if (!isLoopbackMediaUrl(window.location.origin) || !abs) {
      return `${window.location.origin}${relPath}`;
    }
  }

  return abs || relPath || '';
};

export const isMediaImage = (item) =>
  item?.kind === 'image' || String(item?.mime || '').startsWith('image/');

export const isMediaPdf = (item) => {
  const mime = String(item?.mime || '').toLowerCase();
  if (mime === 'application/pdf' || mime.includes('pdf')) return true;
  return /\.pdf$/i.test(String(item?.name || ''));
};

export async function mediaItemToFile(item) {
  const src = mediaSrc(item);
  if (!src) throw new Error('Keine Datei-URL');
  const res = await fetch(src, { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Datei konnte nicht geladen werden');
  const blob = await res.blob();
  const file = new File([blob], item.name || 'datei', {
    type: item.mime || blob.type || 'application/octet-stream',
  });
  const publicId = String(item.publicId || item.public_id || '').trim();
  if (publicId) {
    Object.defineProperty(file, 'mediaPublicId', {
      value: publicId,
      enumerable: true,
      configurable: true,
    });
  }
  return file;
}

/**
 * Bild-Picker mit Ordner-Navigation (gleiche View wie Anhang-Picker).
 * onSelect(url) — absolute öffentliche URL
 * onSelectItem(item) — optional Rohdaten
 */
export const MediaPickerButton = ({
  onSelect,
  onSelectItem,
  excludeIds = [],
  label = 'Aus Bibliothek',
  className = 'btn-secondary text-xs inline-flex items-center',
}) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [publicBaseUrl, setPublicBaseUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const uploadInputRef = useRef(null);
  const folderNameRef = useRef(null);

  const excluded = useMemo(
    () => new Set((Array.isArray(excludeIds) ? excludeIds : []).map((x) => Number(x)).filter(Boolean)),
    [excludeIds]
  );

  const folderById = useMemo(() => {
    const map = new Map();
    folders.forEach((f) => map.set(Number(f.id), f));
    return map;
  }, [folders]);

  const currentFolder = folderId == null ? null : folderById.get(Number(folderId)) || null;

  const breadcrumbs = useMemo(() => {
    const crumbs = [];
    let cur = currentFolder;
    const seen = new Set();
    while (cur && !seen.has(Number(cur.id))) {
      seen.add(Number(cur.id));
      crumbs.unshift(cur);
      cur = cur.parentId != null ? folderById.get(Number(cur.parentId)) : null;
    }
    return crumbs;
  }, [currentFolder, folderById]);

  const childFolders = useMemo(() => {
    if (folderId == null) return folders.filter((f) => f.parentId == null);
    return folders.filter((f) => Number(f.parentId) === Number(folderId));
  }, [folders, folderId]);

  const folderItems = useMemo(() => {
    const list =
      folderId == null
        ? items.filter((i) => i.folderId == null)
        : items.filter((i) => Number(i.folderId) === Number(folderId));
    return [...list]
      .filter((item) => isMediaImage(item) && !excluded.has(Number(item.id)))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'de'));
  }, [items, folderId, excluded]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/media');
      const all = Array.isArray(res.data?.items) ? res.data.items : [];
      setPublicBaseUrl(String(res.data?.publicBaseUrl || '').trim());
      setItems(all);
      setFolders(Array.isArray(res.data?.folders) ? res.data.folders : []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bibliothek nicht erreichbar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    setFolderId(null);
    setFolderModalOpen(false);
    setFolderName('');
    load();
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!folderModalOpen) return undefined;
    const t = window.setTimeout(() => folderNameRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [folderModalOpen]);

  const pickItem = (item) => {
    onSelectItem?.(item);
    onSelect?.(mediaAbsoluteUrl(item, publicBaseUrl));
    setOpen(false);
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    let lastItem = null;
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (folderId != null) fd.append('folderId', String(folderId));
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.post('/api/media', fd);
        const item = res.data?.item;
        if (item) {
          setItems((prev) => [item, ...prev]);
          if (isMediaImage(item)) {
            lastItem = item;
            ok += 1;
          }
        }
      } catch (err) {
        toast.error(err.response?.data?.error || `„${file.name}“ fehlgeschlagen`);
      }
    }
    setUploading(false);
    if (uploadInputRef.current) uploadInputRef.current.value = '';
    if (lastItem) {
      toast.success(ok === 1 ? 'Bild ausgewählt' : `${ok} Bilder hochgeladen`);
      pickItem(lastItem);
    } else if (ok === 0 && files.length) {
      toast.error('Bitte ein Bild hochladen (JPEG, PNG, GIF, WebP)');
    }
  };

  const openCreateFolder = () => {
    setFolderName('');
    setFolderModalOpen(true);
  };

  const submitCreateFolder = async (e) => {
    e?.preventDefault?.();
    const name = folderName.trim();
    if (!name) {
      toast.error('Bitte einen Ordnernamen eingeben');
      return;
    }
    setCreatingFolder(true);
    try {
      const res = await axios.post('/api/media/folders', {
        name,
        parentId: folderId,
      });
      if (res.data?.folder) {
        setFolders((prev) =>
          [...prev, res.data.folder].sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), 'de')
          )
        );
        toast.success('Ordner erstellt');
        setFolderModalOpen(false);
        setFolderName('');
        setFolderId(res.data.folder.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ordner konnte nicht erstellt werden');
    } finally {
      setCreatingFolder(false);
    }
  };

  const emptyHere = !loading && childFolders.length === 0 && folderItems.length === 0;
  const actionsDisabled = uploading || creatingFolder;

  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        <ImageIcon className="w-3.5 h-3.5 mr-1.5" />
        {label}
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[240] flex items-center justify-center p-3 sm:p-4">
            <div
              className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
              onClick={() => !actionsDisabled && setOpen(false)}
            />
            <div className="relative w-full max-w-3xl max-h-[min(88vh,42rem)] bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden flex flex-col scale-in">
              <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-neutral-100 dark:border-neutral-800 flex items-start justify-between gap-3 flex-shrink-0">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Bild aus Bibliothek
                  </h3>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Hochladen, Ordner anlegen oder Bild auswählen ·{' '}
                    <Link
                      to="/media"
                      className="text-primary-600 hover:underline"
                      onClick={() => setOpen(false)}
                    >
                      Bibliothek verwalten
                    </Link>
                  </p>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex-shrink-0"
                  onClick={() => !actionsDisabled && setOpen(false)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-3 sm:px-4 py-2.5 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-950/40 flex items-center gap-2 flex-shrink-0 flex-wrap">
                {folderId != null ? (
                  <button
                    type="button"
                    disabled={actionsDisabled}
                    onClick={() =>
                      setFolderId(currentFolder?.parentId == null ? null : currentFolder.parentId)
                    }
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-300 disabled:opacity-50"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Zurück
                  </button>
                ) : null}
                <nav className="flex items-center gap-0.5 min-w-0 flex-1 overflow-x-auto">
                  <button
                    type="button"
                    onClick={() => setFolderId(null)}
                    className={`inline-flex items-center h-7 px-2 rounded-md text-xs font-medium whitespace-nowrap ${
                      folderId == null
                        ? 'bg-neutral-200/90 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                        : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    }`}
                  >
                    Bibliothek
                  </button>
                  {breadcrumbs.map((crumb) => (
                    <React.Fragment key={crumb.id}>
                      <ChevronRight className="w-3 h-3 text-neutral-300 dark:text-neutral-600 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => setFolderId(crumb.id)}
                        className={`inline-flex items-center h-7 px-2 rounded-md text-xs font-medium whitespace-nowrap ${
                          Number(folderId) === Number(crumb.id)
                            ? 'bg-neutral-200/90 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                            : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                        }`}
                      >
                        {crumb.name}
                      </button>
                    </React.Fragment>
                  ))}
                </nav>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                  <button
                    type="button"
                    disabled={actionsDisabled}
                    onClick={openCreateFolder}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-amber-400/70 hover:text-amber-700 dark:hover:text-amber-300 disabled:opacity-50"
                    title={
                      folderId == null
                        ? 'Neuen Ordner in der Bibliothek anlegen'
                        : `Unterordner in „${currentFolder?.name || 'Ordner'}“ anlegen`
                    }
                  >
                    <FolderPlus className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Ordner</span>
                  </button>
                  <label
                    className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 cursor-pointer ${
                      actionsDisabled ? 'opacity-50 pointer-events-none' : ''
                    }`}
                    title="Bild in den aktuellen Ordner hochladen"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {uploading ? 'Lädt…' : 'Hochladen'}
                    <input
                      ref={uploadInputRef}
                      type="file"
                      accept={MEDIA_IMAGE_ACCEPT}
                      multiple
                      className="hidden"
                      disabled={actionsDisabled}
                      onChange={(e) => uploadFiles(e.target.files)}
                    />
                  </label>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4">
                {loading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <div key={i} className="aspect-square rounded-xl skeleton" />
                    ))}
                  </div>
                ) : emptyHere ? (
                  <div className="flex flex-col items-center justify-center py-14 text-center px-4">
                    <span className="w-12 h-12 rounded-2xl bg-neutral-100 dark:bg-neutral-800 text-neutral-400 flex items-center justify-center mb-3">
                      <ImageIcon className="w-6 h-6" />
                    </span>
                    <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                      {folderId == null ? 'Noch keine Bilder' : 'Keine Bilder in diesem Ordner'}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1 max-w-xs">
                      Lade hier ein Bild hoch oder lege einen Ordner an.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                      <button
                        type="button"
                        disabled={actionsDisabled}
                        onClick={openCreateFolder}
                        className="btn-secondary text-xs inline-flex items-center"
                      >
                        <FolderPlus className="w-3.5 h-3.5 mr-1.5" />
                        Ordner erstellen
                      </button>
                      <label
                        className={`btn-primary text-xs inline-flex items-center cursor-pointer ${
                          actionsDisabled ? 'opacity-50 pointer-events-none' : ''
                        }`}
                      >
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                        Bild hochladen
                        <input
                          type="file"
                          accept={MEDIA_IMAGE_ACCEPT}
                          className="hidden"
                          disabled={actionsDisabled}
                          onChange={(e) => uploadFiles(e.target.files)}
                        />
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {childFolders.map((f) => {
                      const nestedCount = folders.filter((x) => Number(x.parentId) === Number(f.id)).length;
                      const fileCount = items.filter(
                        (x) =>
                          Number(x.folderId) === Number(f.id) &&
                          isMediaImage(x) &&
                          !excluded.has(Number(x.id))
                      ).length;
                      return (
                        <button
                          key={`folder-${f.id}`}
                          type="button"
                          onClick={() => setFolderId(f.id)}
                          className="group rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50/90 dark:bg-neutral-950/50 hover:border-amber-400/70 hover:shadow-sm transition-all text-left overflow-hidden"
                          title={`Öffnen: ${f.name}`}
                        >
                          <div className="aspect-square flex flex-col items-center justify-center gap-2 p-3">
                            <span className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                              <Folder className="w-6 h-6" />
                            </span>
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate w-full text-center px-1">
                              {f.name}
                            </span>
                            <span className="text-[10px] text-neutral-400 tabular-nums">
                              {fileCount} Bild{fileCount === 1 ? '' : 'er'}
                              {nestedCount ? ` · ${nestedCount} Ordner` : ''}
                            </span>
                          </div>
                        </button>
                      );
                    })}

                    {folderItems.map((item) => {
                      const src = mediaSrc(item);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => pickItem(item)}
                          title={item.name}
                          className="group relative rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden text-left transition-all hover:border-primary-500 hover:ring-2 hover:ring-primary-500/25 hover:shadow-sm"
                        >
                          <div className="relative aspect-square bg-neutral-100 dark:bg-neutral-950 flex items-center justify-center">
                            {src ? (
                              <img
                                src={src}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <ImageIcon className="w-8 h-8 text-neutral-300" />
                            )}
                          </div>
                          <div className="px-2 py-1.5 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900">
                            <p className="text-[11px] font-medium text-neutral-800 dark:text-neutral-100 truncate">
                              {item.name}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {folderModalOpen ? (
                <div className="absolute inset-0 z-10 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-[2px]">
                  <form
                    onSubmit={submitCreateFolder}
                    className="w-full max-w-sm rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl p-4 space-y-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">
                        {folderId == null ? 'Neuer Ordner' : 'Neuer Unterordner'}
                      </h4>
                      <p className="text-[11px] text-neutral-500 mt-0.5">
                        {folderId == null
                          ? 'Wird in der Bibliothek angelegt'
                          : `In „${currentFolder?.name || 'Ordner'}“`}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">
                        Ordnername
                      </label>
                      <input
                        ref={folderNameRef}
                        className="input-field"
                        value={folderName}
                        onChange={(e) => setFolderName(e.target.value)}
                        placeholder="z. B. Logos"
                        disabled={creatingFolder}
                        autoFocus
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        disabled={creatingFolder}
                        onClick={() => {
                          setFolderModalOpen(false);
                          setFolderName('');
                        }}
                      >
                        Abbrechen
                      </button>
                      <button type="submit" className="btn-primary text-sm" disabled={creatingFolder}>
                        {creatingFolder ? '…' : 'Erstellen'}
                      </button>
                    </div>
                  </form>
                </div>
              ) : null}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

/**
 * Picker für Anhänge: Ordner-Navigation mit Vorschau, liefert File[].
 * onSelect(files: File[])
 */
export const MediaAttachmentPickerButton = ({
  onSelect,
  label = 'Bibliothek',
  className = 'btn-secondary text-[11px] !py-1 !px-2 inline-flex items-center',
  maxFiles = 10,
}) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const uploadInputRef = useRef(null);
  const folderNameRef = useRef(null);

  const folderById = useMemo(() => {
    const map = new Map();
    folders.forEach((f) => map.set(Number(f.id), f));
    return map;
  }, [folders]);

  const currentFolder = folderId == null ? null : folderById.get(Number(folderId)) || null;

  const breadcrumbs = useMemo(() => {
    const crumbs = [];
    let cur = currentFolder;
    const seen = new Set();
    while (cur && !seen.has(Number(cur.id))) {
      seen.add(Number(cur.id));
      crumbs.unshift(cur);
      cur = cur.parentId != null ? folderById.get(Number(cur.parentId)) : null;
    }
    return crumbs;
  }, [currentFolder, folderById]);

  const childFolders = useMemo(() => {
    if (folderId == null) return folders.filter((f) => f.parentId == null);
    return folders.filter((f) => Number(f.parentId) === Number(folderId));
  }, [folders, folderId]);

  const folderItems = useMemo(() => {
    const list =
      folderId == null
        ? items.filter((i) => i.folderId == null)
        : items.filter((i) => Number(i.folderId) === Number(folderId));
    return [...list].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'de'));
  }, [items, folderId]);

  const selectedItems = useMemo(
    () => items.filter((i) => selected.has(i.id)),
    [items, selected]
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/media');
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setFolders(Array.isArray(res.data?.folders) ? res.data.folders : []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bibliothek nicht erreichbar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    setFolderId(null);
    setSelected(new Set());
    setFolderModalOpen(false);
    setFolderName('');
    load();
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!folderModalOpen) return undefined;
    const t = window.setTimeout(() => folderNameRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [folderModalOpen]);

  const formatSize = (bytes) => {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fileExt = (item) => {
    const name = String(item?.name || '');
    const dot = name.lastIndexOf('.');
    if (dot >= 0 && dot < name.length - 1) return name.slice(dot + 1).toUpperCase().slice(0, 5);
    const mime = String(item?.mime || '');
    if (mime.includes('pdf')) return 'PDF';
    return 'FILE';
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= maxFiles) {
          toast.error(`Maximal ${maxFiles} Dateien`);
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    const uploadedIds = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (folderId != null) fd.append('folderId', String(folderId));
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.post('/api/media', fd);
        if (res.data?.item) {
          setItems((prev) => [res.data.item, ...prev]);
          uploadedIds.push(res.data.item.id);
          ok += 1;
        }
      } catch (err) {
        toast.error(err.response?.data?.error || `„${file.name}“ fehlgeschlagen`);
      }
    }
    setUploading(false);
    if (uploadInputRef.current) uploadInputRef.current.value = '';
    if (ok) {
      toast.success(ok === 1 ? 'Datei hochgeladen' : `${ok} Dateien hochgeladen`);
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of uploadedIds) {
          if (next.size >= maxFiles) break;
          next.add(id);
        }
        return next;
      });
    }
  };

  const openCreateFolder = () => {
    setFolderName('');
    setFolderModalOpen(true);
  };

  const submitCreateFolder = async (e) => {
    e?.preventDefault?.();
    const name = folderName.trim();
    if (!name) {
      toast.error('Bitte einen Ordnernamen eingeben');
      return;
    }
    setCreatingFolder(true);
    try {
      const res = await axios.post('/api/media/folders', {
        name,
        parentId: folderId,
      });
      if (res.data?.folder) {
        setFolders((prev) =>
          [...prev, res.data.folder].sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), 'de')
          )
        );
        toast.success('Ordner erstellt');
        setFolderModalOpen(false);
        setFolderName('');
        setFolderId(res.data.folder.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ordner konnte nicht erstellt werden');
    } finally {
      setCreatingFolder(false);
    }
  };

  const confirm = async () => {
    const picked = items.filter((i) => selected.has(i.id));
    if (!picked.length) {
      toast.error('Bitte Dateien auswählen');
      return;
    }
    setBusy(true);
    try {
      const files = [];
      for (const item of picked) {
        // eslint-disable-next-line no-await-in-loop
        files.push(await mediaItemToFile(item));
      }
      onSelect?.(files);
      setOpen(false);
      toast.success(files.length === 1 ? 'Datei übernommen' : `${files.length} Dateien übernommen`);
    } catch (err) {
      toast.error(err.message || 'Auswahl fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const emptyHere = !loading && childFolders.length === 0 && folderItems.length === 0;
  const actionsDisabled = busy || uploading || creatingFolder;

  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        <Paperclip className="w-3 h-3 mr-1" />
        {label}
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[240] flex items-center justify-center p-3 sm:p-4">
            <div
              className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
              onClick={() => !actionsDisabled && setOpen(false)}
            />
            <div className="relative w-full max-w-3xl max-h-[min(88vh,42rem)] bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden flex flex-col scale-in">
              <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-neutral-100 dark:border-neutral-800 flex items-start justify-between gap-3 flex-shrink-0">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Anhang aus Bibliothek
                  </h3>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Hochladen, Ordner anlegen oder vorhandene Dateien wählen ·{' '}
                    <Link
                      to="/media"
                      className="text-primary-600 hover:underline"
                      onClick={() => setOpen(false)}
                    >
                      Bibliothek verwalten
                    </Link>
                  </p>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex-shrink-0"
                  onClick={() => !actionsDisabled && setOpen(false)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-3 sm:px-4 py-2.5 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-950/40 flex items-center gap-2 flex-shrink-0 flex-wrap">
                {folderId != null ? (
                  <button
                    type="button"
                    disabled={actionsDisabled}
                    onClick={() =>
                      setFolderId(currentFolder?.parentId == null ? null : currentFolder.parentId)
                    }
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-300 disabled:opacity-50"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Zurück
                  </button>
                ) : null}
                <nav className="flex items-center gap-0.5 min-w-0 flex-1 overflow-x-auto">
                  <button
                    type="button"
                    onClick={() => setFolderId(null)}
                    className={`inline-flex items-center h-7 px-2 rounded-md text-xs font-medium whitespace-nowrap ${
                      folderId == null
                        ? 'bg-neutral-200/90 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                        : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    }`}
                  >
                    Bibliothek
                  </button>
                  {breadcrumbs.map((crumb) => (
                    <React.Fragment key={crumb.id}>
                      <ChevronRight className="w-3 h-3 text-neutral-300 dark:text-neutral-600 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => setFolderId(crumb.id)}
                        className={`inline-flex items-center h-7 px-2 rounded-md text-xs font-medium whitespace-nowrap ${
                          Number(folderId) === Number(crumb.id)
                            ? 'bg-neutral-200/90 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                            : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                        }`}
                      >
                        {crumb.name}
                      </button>
                    </React.Fragment>
                  ))}
                </nav>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                  <button
                    type="button"
                    disabled={actionsDisabled}
                    onClick={openCreateFolder}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-amber-400/70 hover:text-amber-700 dark:hover:text-amber-300 disabled:opacity-50"
                    title={
                      folderId == null
                        ? 'Neuen Ordner in der Bibliothek anlegen'
                        : `Unterordner in „${currentFolder?.name || 'Ordner'}“ anlegen`
                    }
                  >
                    <FolderPlus className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Ordner</span>
                  </button>
                  <label
                    className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 cursor-pointer ${
                      actionsDisabled ? 'opacity-50 pointer-events-none' : ''
                    }`}
                    title="Datei(en) in den aktuellen Ordner hochladen"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {uploading ? 'Lädt…' : 'Hochladen'}
                    <input
                      ref={uploadInputRef}
                      type="file"
                      multiple
                      accept={MEDIA_FILE_ACCEPT}
                      className="hidden"
                      disabled={actionsDisabled}
                      onChange={(e) => uploadFiles(e.target.files)}
                    />
                  </label>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4">
                {loading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <div key={i} className="aspect-square rounded-xl skeleton" />
                    ))}
                  </div>
                ) : emptyHere ? (
                  <div className="flex flex-col items-center justify-center py-14 text-center px-4">
                    <span className="w-12 h-12 rounded-2xl bg-neutral-100 dark:bg-neutral-800 text-neutral-400 flex items-center justify-center mb-3">
                      <Folder className="w-6 h-6" />
                    </span>
                    <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                      {folderId == null ? 'Noch keine Dateien' : 'Dieser Ordner ist leer'}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1 max-w-xs">
                      Lade hier Dateien hoch oder lege einen Ordner an.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                      <button
                        type="button"
                        disabled={actionsDisabled}
                        onClick={openCreateFolder}
                        className="btn-secondary text-xs inline-flex items-center"
                      >
                        <FolderPlus className="w-3.5 h-3.5 mr-1.5" />
                        Ordner erstellen
                      </button>
                      <label
                        className={`btn-primary text-xs inline-flex items-center cursor-pointer ${
                          actionsDisabled ? 'opacity-50 pointer-events-none' : ''
                        }`}
                      >
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                        Datei hochladen
                        <input
                          type="file"
                          multiple
                          accept={MEDIA_FILE_ACCEPT}
                          className="hidden"
                          disabled={actionsDisabled}
                          onChange={(e) => uploadFiles(e.target.files)}
                        />
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {childFolders.map((f) => {
                      const nestedCount = folders.filter((x) => Number(x.parentId) === Number(f.id)).length;
                      const fileCount = items.filter((x) => Number(x.folderId) === Number(f.id)).length;
                      return (
                        <button
                          key={`folder-${f.id}`}
                          type="button"
                          onClick={() => setFolderId(f.id)}
                          className="group rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50/90 dark:bg-neutral-950/50 hover:border-amber-400/70 hover:shadow-sm transition-all text-left overflow-hidden"
                          title={`Öffnen: ${f.name}`}
                        >
                          <div className="aspect-square flex flex-col items-center justify-center gap-2 p-3">
                            <span className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                              <Folder className="w-6 h-6" />
                            </span>
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate w-full text-center px-1">
                              {f.name}
                            </span>
                            <span className="text-[10px] text-neutral-400 tabular-nums">
                              {fileCount} Datei{fileCount === 1 ? '' : 'en'}
                              {nestedCount ? ` · ${nestedCount} Ordner` : ''}
                            </span>
                          </div>
                        </button>
                      );
                    })}

                    {folderItems.map((item) => {
                      const active = selected.has(item.id);
                      const image = isMediaImage(item);
                      const src = mediaSrc(item);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggle(item.id)}
                          title={item.name}
                          className={`group relative rounded-xl border overflow-hidden text-left transition-all ${
                            active
                              ? 'border-primary-500 ring-2 ring-primary-500/25 shadow-md'
                              : 'border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-sm'
                          }`}
                        >
                          <div className="relative aspect-square bg-neutral-100 dark:bg-neutral-950">
                            {image && src ? (
                              <img
                                src={src}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-neutral-100 to-neutral-50 dark:from-neutral-900 dark:to-neutral-950">
                                <span className="w-10 h-10 rounded-lg bg-white dark:bg-neutral-800 shadow-sm flex items-center justify-center text-neutral-500">
                                  <FileText className="w-5 h-5" />
                                </span>
                                <span className="text-[10px] font-bold tracking-wider text-neutral-400">
                                  {fileExt(item)}
                                </span>
                              </div>
                            )}
                            <span
                              className={`absolute top-2 left-2 flex h-5 w-5 items-center justify-center rounded-md border shadow-sm ${
                                active
                                  ? 'bg-primary-600 border-primary-600 text-white'
                                  : 'bg-white/95 dark:bg-neutral-900/90 border-neutral-300 dark:border-neutral-600'
                              }`}
                            >
                              {active ? <Check className="w-3 h-3" /> : null}
                            </span>
                          </div>
                          <div className="px-2 py-1.5 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900">
                            <p className="text-[11px] font-medium text-neutral-800 dark:text-neutral-100 truncate">
                              {item.name}
                            </p>
                            <p className="text-[10px] text-neutral-400 truncate">
                              {formatSize(item.size)}
                              {item.mime ? ` · ${String(item.mime).split('/')[1] || item.mime}` : ''}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedItems.length > 0 ? (
                <div className="px-3 sm:px-4 py-2.5 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/90 dark:bg-neutral-950/50 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-[11px] font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wide">
                      Auswahl · Vorschau
                    </p>
                    <span className="text-[11px] text-neutral-400 tabular-nums">
                      {selectedItems.length}/{maxFiles}
                    </span>
                    <button
                      type="button"
                      className="ml-auto text-[11px] text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
                      onClick={() => setSelected(new Set())}
                    >
                      Leeren
                    </button>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-0.5">
                    {selectedItems.map((item) => {
                      const image = isMediaImage(item);
                      const src = mediaSrc(item);
                      return (
                        <div
                          key={`sel-${item.id}`}
                          className="relative flex-shrink-0 w-14 group"
                          title={item.name}
                        >
                          <div className="w-14 h-14 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-neutral-100 dark:bg-neutral-950">
                            {image && src ? (
                              <img src={src} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center gap-0.5">
                                <FileText className="w-4 h-4 text-neutral-400" />
                                <span className="text-[8px] font-bold text-neutral-400">{fileExt(item)}</span>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => toggle(item.id)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-neutral-800 text-white flex items-center justify-center shadow opacity-90 hover:opacity-100"
                            title="Entfernen"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <p className="mt-1 text-[9px] text-neutral-500 truncate text-center leading-tight">
                            {item.name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="px-4 sm:px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between gap-2 flex-shrink-0">
                <span className="text-xs text-neutral-500 tabular-nums">
                  {selected.size === 0
                    ? 'Keine Datei ausgewählt'
                    : `${selected.size} Datei${selected.size === 1 ? '' : 'en'} ausgewählt`}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={actionsDisabled}
                    onClick={() => setOpen(false)}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    className="btn-primary text-sm"
                    disabled={actionsDisabled || selected.size === 0}
                    onClick={confirm}
                  >
                    {busy ? '…' : 'Übernehmen'}
                  </button>
                </div>
              </div>

              {folderModalOpen ? (
                <div className="absolute inset-0 z-10 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-[2px]">
                  <form
                    onSubmit={submitCreateFolder}
                    className="w-full max-w-sm rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl p-4 space-y-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">
                        {folderId == null ? 'Neuer Ordner' : 'Neuer Unterordner'}
                      </h4>
                      <p className="text-[11px] text-neutral-500 mt-0.5">
                        {folderId == null
                          ? 'Wird in der Bibliothek angelegt'
                          : `In „${currentFolder?.name || 'Ordner'}“`}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">
                        Ordnername
                      </label>
                      <input
                        ref={folderNameRef}
                        className="input-field"
                        value={folderName}
                        onChange={(e) => setFolderName(e.target.value)}
                        placeholder="z. B. Angebote"
                        disabled={creatingFolder}
                        autoFocus
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        disabled={creatingFolder}
                        onClick={() => {
                          setFolderModalOpen(false);
                          setFolderName('');
                        }}
                      >
                        Abbrechen
                      </button>
                      <button type="submit" className="btn-primary text-sm" disabled={creatingFolder}>
                        {creatingFolder ? '…' : 'Erstellen'}
                      </button>
                    </div>
                  </form>
                </div>
              ) : null}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MediaPickerButton;
